import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { KeyClassroomEngagementReportResult } from 'types/schema-generated-types';
import AllModuleAssessmentAcceleratorLayout from '../../../AccessmentAcceleratorComponents/AllModuleAssessmentAcceleratorLayout';
import AssessmentStatusDetails from '../../../AccessmentAcceleratorComponents/AssessmentStatusDetails';
import ModuleTabSelector from '../../../AccessmentAcceleratorComponents/ModuleTabSelector';
import {
  buildClassAssessmentHeaderList,
  buildSingleClassroomModuleData,
  hasSingleModuleData,
  mapModuleStudents
} from '../../../keyboarding-report-util';

import MaybeShowErrorMessageWithLoading from 'components/ReportsModule/components/common/MaybeShowErrorMessageWithLoading';
import AllStudentChartClassBreakDown from '../../../AccessmentAcceleratorComponents/AllStudentChartClassBreakDown';
import SingleModuleClassroomChart from '../../../AccessmentAcceleratorComponents/SingleModuleClassroomChart';
import AllModuleClassroomChart from '../../../AccessmentAcceleratorComponents/AllModuleClassroomChart';
import useModuleTabSelector from '../../../hooks/use-module-tab-selector';
import useBooleanSwitch from '../../../hooks/use-boolean-switch';

type Props = {
  loading?: boolean;
  classroomReport?: KeyClassroomEngagementReportResult;
};

const ClassroomAssessmentAcceleratorChart: React.FC<Props> = ({ loading, classroomReport }) => {
  const { t } = useTranslation('ReportsHelpCards');
  const [{ showAll, selectedModule }, { setSelectedModule }] = useModuleTabSelector();

  const singleModuleData: any = buildSingleClassroomModuleData(classroomReport!, selectedModule);
  const headerList = showAll ? classroomReport?.assessmentAccelerator : singleModuleData;

  const getStudentData = useBooleanSwitch(classroomReport, {
    students: mapModuleStudents(classroomReport?.students!, selectedModule)
  });

  const getShouldRender = useBooleanSwitch(
    classroomReport?.assessmentAccelerator?.activityTypes?.some(at => at.modules.length > 0),
    singleModuleData.data.some((m: any) => hasSingleModuleData(m))
  );

  const TopComponent = useBooleanSwitch(
    <AllModuleClassroomChart reportData={classroomReport?.assessmentAccelerator!} />,
    <SingleModuleClassroomChart reportData={classroomReport} selectedModule={selectedModule} />
  );

  return (
    <ReportCard
      title={t('Assessment Accelerator')}
      helpText={t('assessmentAccelerator', { ns: 'ReportsHelpCards' })}
      loading={loading!}
      reportData={classroomReport?.assessmentAccelerator?.activityTypes!}
      endAdornment={
        <ModuleTabSelector onChange={setSelectedModule} selectedModule={selectedModule} />
      }
    >
      <MaybeShowErrorMessageWithLoading shouldRender={getShouldRender(showAll)} loading={loading!}>
        <AllModuleAssessmentAcceleratorLayout
          OutsideHeader={
            <AssessmentStatusDetails
              headerList={buildClassAssessmentHeaderList(headerList, t)}
              title={selectedModule}
            />
          }
          TopComponent={TopComponent(showAll)}
          BottomComponent={<AllStudentChartClassBreakDown reportData={getStudentData(showAll)} />}
          showBottomDivider={false}
          showDivider={false}
          headerList={headerList}
          title={selectedModule}
        />
      </MaybeShowErrorMessageWithLoading>
    </ReportCard>
  );
};

export default ClassroomAssessmentAcceleratorChart;
